/**
 * One control per field kind — `docs/list-filters.md` §4.2.
 *
 * The rule the whole filter rests on: **a screen never builds a filter
 * control.** The server's catalogue says a column is an enum, or money, or
 * points at a crew, and that is what decides whether you get chips, two
 * boxes, or a mini search. Adding a column to an `AreaDef` puts it in the
 * quick popover, the advanced builder and the card builder with the right
 * control and no client change at all.
 *
 * Each control edits *one* condition for its field, or none. That is a
 * narrower job than the grammar allows — the advanced builder can hold two
 * conditions on the same column — and it is what makes the quick popover
 * legible: a section is a question, and a question has one answer.
 *
 * These are also what `filter-value.tsx` renders inside the advanced builder,
 * so the two editors cannot drift into disagreeing about what "is any of"
 * looks like.
 *
 * Pixel reference: `design/mockups/30-list-filters/html/filters-02-kinds.html`.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  DATE_PRESET_LABELS, NULLARY_OPERATORS, TOKEN_LABELS, datePeriod, describePeriod,
  describeRelative, isToken, labelFor, pruneLabels, relativeWindow, unseenText,
  type DatePreset, type FilterCondition, type FilterGroup, type FilterOperator,
  type FilterToken, type RelativeWindow,
} from '@rowted/shared'
import { Chip, Segmented, Checkbox, Avatar } from './primitives.js'
import { MenuSelect } from './menu-select.js'
import { DateWindowButton } from './date-picker.js'
import { IconSearch } from '../frame/icons.js'
import { cn } from '../lib/cn.js'
import { formatNumber } from '../lib/format.js'
import type { ListFieldDef, UseListResult } from '../lib/use-list.js'

/** Every control gets the same four things, and answers with a condition. */
export interface KindControlProps {
  list: UseListResult
  field: ListFieldDef
  /** This field's one condition, or null when it has none. */
  value: FilterCondition | null
  /** `null` clears the field — "Any". */
  onChange: (next: FilterCondition | null) => void
  /**
   * The rest of the filter, for the counts.
   *
   * Everything except this field's own condition. A count beside a chip is
   * only worth having if it is the count you would get by ticking it, and
   * against the whole table "Quoted 212" on a list already narrowed to one
   * branch is a number from a different question.
   */
  narrowedBy?: FilterGroup
}

// ---------------------------------------------------------------------------
// The values a column holds, counted
// ---------------------------------------------------------------------------

export interface CountedValue { value: string; count: number }

/**
 * `POST /<area>/distinct`, cached for as long as the control is open.
 *
 * Keyed on the narrowing filter as well as the field, because the counts move
 * when the rest of the filter does — that is the point of them.
 */
function useCountedValues(
  list: UseListResult, field: ListFieldDef, narrowedBy: FilterGroup | undefined,
): { values: CountedValue[]; loading: boolean } {
  const [values, setValues] = useState<CountedValue[]>([])
  const [loading, setLoading] = useState(true)
  const key = JSON.stringify(narrowedBy?.conditions ?? [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    void list.countedValues(field.name, narrowedBy)
      .then((result) => { if (!cancelled) { setValues(result); setLoading(false) } })
      .catch(() => { if (!cancelled) { setValues([]); setLoading(false) } })
    return () => { cancelled = true }
    /*
     * `key` rather than the filter object. A fresh `{...}` on every render
     * has a new identity every time, so depending on it would refetch the
     * counts on every keystroke anywhere else in the popover — and the
     * counts would flicker while somebody typed in a different section.
     */
  }, [field.name, key, list, narrowedBy])

  return { values, loading }
}

/** "in_progress" as it is stored; "In progress" as it reads. */
export const humanValue = (value: unknown): string => String(value ?? '')
  .replace(/_/g, ' ')
  .replace(/^./, (first) => first.toUpperCase())

/**
 * What an enum value is *called*, when its field says.
 *
 * `PA` is what is stored and what a column shows, because a column of codes
 * lines up and sorts as one thing. Anywhere with room for the sentence — a
 * group header, a readback — says "PA · Pennsylvania", and both read this,
 * so they cannot disagree about what PA is. A value the field does not name
 * prints as itself rather than as blank: an unrecognised state is somebody's
 * data, and hiding it is how it never gets fixed.
 */
export function optionLabel(field: ListFieldDef | undefined, value: unknown): string {
  const raw = String(value ?? '')
  const option = field?.options?.find((entry) => entry.value === raw)
  if (!option) return raw
  return option.sub ? `${option.label} · ${option.sub}` : option.label
}

/**
 * The semantic dot beside a value, where the product has one for it.
 *
 * Read from the same CSS variables `StatusChip` uses, so a status is the same
 * colour in a filter chip as it is in the table underneath. Keyed by area and
 * field because `status` means different things on different lists: a job's
 * Completed is green and an invoice's Paid is a different green, and one
 * lookup table for both would print the wrong one somewhere.
 */
const TONE_GROUPS: Record<string, string> = {
  'jobs.status': 'job',
  'visits.status': 'job',
  'invoices.status': 'invoice',
  'equipment.status': 'equipment',
  'jobs.divisionName': 'serviceLine',
  'visits.divisionName': 'serviceLine',
}

function toneFor(area: string, field: string, value: string): string | null {
  const group = TONE_GROUPS[`${area}.${field}`]
  if (!group) return null
  // The fallback keeps an unrecognised value from painting the dot
  // transparent: a value the seed has and the token build does not is a
  // grey dot, not an invisible one.
  return `rgb(var(--status-${group}-${value}-fg, var(--color-line-strong)))`
}

// ---------------------------------------------------------------------------
// Enum
// ---------------------------------------------------------------------------

/** Above this many values the tail folds behind "+ N more". */
const CHIP_LIMIT = 8

export function EnumControl({ list, field, value, onChange, narrowedBy }: KindControlProps) {
  const { values, loading } = useCountedValues(list, field, narrowedBy)
  const [expanded, setExpanded] = useState(false)

  const picked = useMemo(() => {
    if (!value) return [] as string[]
    return Array.isArray(value.value) ? value.value.map(String) : [String(value.value)]
  }, [value])

  // `not_in` is the same chips read the other way round, so the segment below
  // flips the operator and leaves the selection alone.
  const negated = value?.op === 'not_in'

  const write = (next: string[], op: FilterOperator = negated ? 'not_in' : 'in') => {
    // Nothing selected is not "match nothing" — it is no condition at all.
    onChange(next.length === 0 ? null : { field: field.name, op, value: next })
  }

  const toggle = (entry: string) => write(picked.includes(entry)
    ? picked.filter((one) => one !== entry)
    : [...picked, entry])

  /*
   * A value that is picked is always drawn, wherever it fell in the counts.
   * Otherwise ticking the ninth value would tick a chip and hide it.
   */
  const shown = expanded ? values : (() => {
    const head = values.slice(0, CHIP_LIMIT)
    const missing = values.filter((entry) =>
      picked.includes(entry.value) && !head.includes(entry))
    return [...head, ...missing]
  })()
  const hidden = values.length - shown.length

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex flex-wrap items-center gap-1.5">
        {values.length === 0 && (
          <span className="text-sub text-text-faint">
            {loading ? 'Counting…' : 'No values on this list.'}
          </span>
        )}
        {shown.map((entry) => {
          const on = picked.includes(entry.value)
          const dot = toneFor(list.area, field.name, entry.value)
          return (
            <Chip
              key={entry.value}
              size="control"
              active={on}
              onClick={() => toggle(entry.value)}
            >
              {dot && (
                <span
                  aria-hidden
                  className="h-[7px] w-[7px] shrink-0 rounded-full"
                  style={{ background: dot }}
                />
              )}
              {humanValue(entry.value)}
              <span className={cn('text-sub font-medium tnum',
                on ? 'text-accent' : 'text-text-faint')}
              >
                {formatNumber(entry.count)}
              </span>
            </Chip>
          )
        })}
        {hidden > 0 && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="pl-0.5 text-cell font-semibold text-accent hover:text-accent-dark"
          >
            + {hidden} more
          </button>
        )}
      </div>

      {/*
        * Only once something is picked. "Any of / None of" over an empty
        * selection is a choice between two ways of saying nothing.
        */}
      {picked.length > 0 && (
        <div className="flex items-center gap-2.5">
          <Segmented
            size="large"
            full
            label={`How to match ${field.label}`}
            value={negated ? 'not_in' : 'in'}
            onChange={(next) => write(picked, next as FilterOperator)}
            options={[{ value: 'in', label: 'Any of' }, { value: 'not_in', label: 'None of' }]}
          />
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Date
// ---------------------------------------------------------------------------

/**
 * The eight presets the quick filter offers, in board 5's order.
 *
 * Not all twenty-one `DATE_PRESETS`: the weeks, the quarters and the three
 * "to date" windows are in the picker's rail one click away. Eight chips is a
 * row and a half; twenty-one is a grid nobody reads, and the fast path exists
 * to save a click rather than to list everything.
 *
 * (The hyphenated form of that phrase is deliberately not written here. The
 * token guard scans source text rather than class attributes — it has to,
 * because classes are composed with `cn()` and template strings — so an
 * ordinary hyphenated English word that happens to look like a Tailwind
 * gradient stop reads as an undefined colour. Rewording a comment is cheaper
 * than loosening a guard, and the guard is the thing that has to stay
 * suspicious.)
 */
const QUICK_PRESETS: DatePreset[] = [
  'today', 'yesterday', 'last_7_days', 'last_30_days',
  'this_month', 'last_month', 'next_30_days', 'this_year',
]

/** The second row: the windows that are not a calendar period. */
const DATE_SHORTCUTS: { label: string; build: () => FilterCondition['op'] extends never
  ? never : { op: FilterOperator; value: unknown } }[] = [
  { label: 'Is empty', build: () => ({ op: 'is_null', value: undefined }) },
  /*
   * **The token, not today's date** (v3 §5.4's "moves with today").
   *
   * It wrote the literal day the chip was clicked, so "Before today" saved
   * into a view meant "before the 7th of October" for ever — a filter whose
   * name and whose behaviour part company the next morning, silently. The
   * `today` token is resolved by `resolveValue` when the query runs, which
   * is the whole reason tokens exist.
   */
  { label: 'Before today', build: () => ({ op: 'lt', value: { token: 'today' } }) },
  {
    label: 'In the next 14 days',
    build: () => ({
      op: 'in_relative',
      value: { unit: 'days', amount: 14, direction: 'future' } satisfies RelativeWindow,
    }),
  },
]

export function DateControl({ list, field, value, onChange }: KindControlProps) {
  const period = value?.op === 'in_period' ? datePeriod.safeParse(value.value) : null
  const preset = period?.success && 'preset' in period.data ? period.data.preset : null

  const shortcutOn = (label: string): boolean => {
    if (!value) return false
    const built = DATE_SHORTCUTS.find((entry) => entry.label === label)?.build()
    if (!built) return false
    if (built.op !== value.op) return false
    return built.op === 'is_null' || JSON.stringify(built.value) === JSON.stringify(value.value)
  }

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex flex-wrap items-center gap-1.5">
        {QUICK_PRESETS.map((entry) => (
          <Chip
            key={entry}
            size="control"
            active={preset === entry}
            onClick={() => onChange(preset === entry
              ? null
              : { field: field.name, op: 'in_period', value: { preset: entry } })}
          >
            {DATE_PRESET_LABELS[entry]}
          </Chip>
        ))}
      </div>

      {/*
        * **Pick dates…** rather than **Custom…** (v3 §5.3).
        *
        * "Custom" swapped the chips for two bare date boxes, which is a third
        * control for a question the chips above are already answering — and
        * it could not show you that "Last 30 days" means Aug 23 to Sep 21,
        * which is the one thing somebody checking a filter wants to see. The
        * picker is presets, a calendar and a count-back row all showing the
        * same two days, so whichever way you come at it the other two follow.
        */}
      <DateWindowButton
        list={list}
        label={field.label}
        field={field.name}
        value={value ?? null}
        onChange={onChange}
      />

      <div className="flex flex-wrap items-center gap-1.5">
        {DATE_SHORTCUTS.map((entry) => {
          const on = shortcutOn(entry.label)
          return (
            <Chip
              key={entry.label}
              size="control"
              active={on}
              onClick={() => {
                if (on) { onChange(null); return }
                const built = entry.build()
                onChange({ field: field.name, op: built.op, value: built.value })
              }}
            >
              {entry.label}
            </Chip>
          )
        })}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Relation
// ---------------------------------------------------------------------------

export interface LookupRow { id: string; label: string; sub: string | null; count: number }

export function RelationControl({
  list, field, value, onChange, narrowedBy,
}: KindControlProps) {
  const [query, setQuery] = useState('')
  const [rows, setRows] = useState<LookupRow[]>([])
  const key = JSON.stringify(narrowedBy?.conditions ?? [])

  /** Ids and tokens currently picked, as written. */
  const picked = useMemo<unknown[]>(() => {
    if (!value) return []
    return Array.isArray(value.value) ? value.value : [value.value]
  }, [value])

  const pickedIds = picked.filter((one): one is string => typeof one === 'string')
  const pickedTokens = picked.filter(isToken).map((one) => one.token)

  useEffect(() => {
    let cancelled = false
    const timer = setTimeout(() => {
      void list.lookup(field.name, query, narrowedBy)
        .then((result) => { if (!cancelled) setRows(result) })
        .catch(() => { if (!cancelled) setRows([]) })
    }, query ? 180 : 0)
    return () => { cancelled = true; clearTimeout(timer) }
    // `key`, for the reason `useCountedValues` gives above.
  }, [field.name, query, key, list, narrowedBy])

  /*
   * **Ids and their names, in the same write** (v3 §4.2).
   *
   * Every row in `rows` already carries its label — it is what the list
   * draws — so remembering it here costs nothing and is the only moment it
   * is free. Resolving names afterwards means a round trip per filter per
   * surface, and for a saved view opened by somebody who cannot see one of
   * the records, no answer at all.
   *
   * `pruneLabels` drops the names of ids that are no longer picked, so the
   * condition does not accumulate the history of everything ever ticked.
   */
  const write = (next: unknown[], names: Record<string, string>) => {
    if (next.length === 0) { onChange(null); return }
    const kept = { ...(value?.labels ?? {}), ...names }
    onChange(pruneLabels({ field: field.name, op: 'in', value: next, labels: kept }))
  }

  const nameOf = (id: string) => rows.find((row) => row.id === id)?.label

  const toggleId = (id: string) => {
    const already = pickedIds.includes(id)
    const next = already ? picked.filter((one) => one !== id) : [...picked, id]
    const label = already ? undefined : nameOf(id)
    write(next, label === undefined ? {} : { [id]: label })
  }

  /* A token needs no label — the client renders "Me" from the token itself. */
  const toggleToken = (token: FilterToken) => write(pickedTokens.includes(token)
    ? picked.filter((one) => !(isToken(one) && one.token === token))
    : [...picked, { token }], {})

  /*
   * Ticked rows stay at the top, whatever the search says. Typing a name and
   * watching the thing you already picked scroll out of the list is how you
   * lose track of what you have chosen.
   */
  const ordered = [
    ...rows.filter((row) => pickedIds.includes(row.id)),
    ...rows.filter((row) => !pickedIds.includes(row.id)),
  ]

  return (
    <div className="flex flex-col gap-2">
      <label className={cn('flex h-[34px] items-center gap-2 rounded-control border border-line',
        'bg-surface px-2.5 text-cell text-text focus-within:ring-2 focus-within:ring-accent-ring')}
      >
        <span aria-hidden className="shrink-0 text-text-muted"><IconSearch size={14} /></span>
        <input
          aria-label={`Find a ${field.relation?.label ?? field.label}`}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={`Find a ${(field.relation?.label ?? field.label).toLowerCase()}…`}
          className="w-full min-w-0 bg-transparent text-cell text-text placeholder:text-text-muted focus:outline-none focus-visible:shadow-none"
        />
      </label>

      <ul className="flex list-none flex-col">
        {ordered.length === 0 && (
          <li className="py-1 text-sub text-text-faint">
            {query ? 'Nothing matches.' : 'Nothing to pick from.'}
          </li>
        )}
        {ordered.map((row) => (
          <li key={row.id}>
            <button
              type="button"
              onClick={() => toggleId(row.id)}
              className="flex h-9 w-full items-center gap-2 rounded-field px-1 text-left hover:bg-surface-alt"
            >
              {/*
                * `aria-hidden`: the button around it already carries the
                * row's name, and a labelled checkbox inside a labelled button
                * is the same words twice to a screen reader — and a control
                * inside a control, which is not valid either way. The tick is
                * what the state looks like; the button is what it is.
                */}
              <span aria-hidden className="pointer-events-none flex shrink-0">
                <Checkbox
                  checked={pickedIds.includes(row.id)}
                  onChange={() => toggleId(row.id)}
                  label={row.label}
                  hideLabel
                />
              </span>
              <Avatar name={row.label} size={22} />
              <span className="min-w-0 flex-1 truncate text-cell font-semibold text-text">
                {row.label}
              </span>
              {row.sub && <span className="shrink-0 text-sub text-text-muted">{row.sub}</span>}
              <span className="tnum shrink-0 text-sub text-text-faint">
                {formatNumber(row.count)}
              </span>
            </button>
          </li>
        ))}
      </ul>

      {/*
        * Tokens under the rows, because they are a different kind of answer:
        * a row is one crew, and `Me` is whoever opens the view. That is the
        * whole reason a shared view can be useful to more than one person.
        */}
      {(field.tokens ?? []).length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {(field.tokens ?? []).map((token) => (
            <Chip
              key={token}
              size="control"
              active={pickedTokens.includes(token)}
              onClick={() => toggleToken(token)}
            >
              {TOKEN_LABELS[token]}
            </Chip>
          ))}
          <Chip
            size="control"
            active={value?.op === 'is_null'}
            onClick={() => onChange(value?.op === 'is_null'
              ? null
              : { field: field.name, op: 'is_null' })}
          >
            Unassigned
          </Chip>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Money and number
// ---------------------------------------------------------------------------

export function RangeControl({ field, value, onChange }: KindControlProps) {
  const money = field.kind === 'money'

  const bounds = (() => {
    if (!value) return { from: '', to: '' }
    if (value.op === 'between' && Array.isArray(value.value)) {
      return { from: String(value.value[0] ?? ''), to: String(value.value[1] ?? '') }
    }
    if (value.op === 'gte') return { from: String(value.value ?? ''), to: '' }
    if (value.op === 'lte') return { from: '', to: String(value.value ?? '') }
    return { from: '', to: '' }
  })()

  const write = (from: string, to: string) => {
    const low = from.trim()
    const high = to.trim()
    if (!low && !high) { onChange(null); return }
    if (low && high) {
      onChange({ field: field.name, op: 'between', value: [Number(low), Number(high)] })
    } else if (low) {
      onChange({ field: field.name, op: 'gte', value: Number(low) })
    } else {
      onChange({ field: field.name, op: 'lte', value: Number(high) })
    }
  }

  const presetOn = (preset: NonNullable<ListFieldDef['presets']>[number]): boolean =>
    value?.op === preset.op && JSON.stringify(value.value) === JSON.stringify(preset.value)

  return (
    <div className="flex flex-col gap-2.5">
      <div className="grid grid-cols-[1fr_24px_1fr] items-center gap-1.5">
        <NumberBox
          money={money}
          label={`${field.label} from`}
          value={bounds.from}
          onChange={(next) => write(next, bounds.to)}
        />
        <span className="text-center text-text-faint">–</span>
        <NumberBox
          money={money}
          label={`${field.label} to`}
          value={bounds.to}
          onChange={(next) => write(bounds.from, next)}
        />
      </div>

      {(field.presets ?? []).length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {(field.presets ?? []).map((preset) => (
            <Chip
              key={preset.label}
              size="control"
              active={presetOn(preset)}
              onClick={() => onChange(presetOn(preset)
                ? null
                : { field: field.name, op: preset.op, value: preset.value })}
            >
              {preset.label}
            </Chip>
          ))}
        </div>
      )}
    </div>
  )
}

function NumberBox({
  money, label, value, onChange,
}: { money: boolean; label: string; value: string; onChange: (next: string) => void }) {
  return (
    /*
     * `aria-label` on the input rather than a hidden span inside the label.
     * The `$` is inside the box too, so a label's text content would be
     * "$Expected from" — an accessible name nobody would guess and no test
     * can match.
     */
    <label className={cn('flex h-[34px] items-center rounded-control border border-line bg-surface',
      'px-[11px] focus-within:ring-2 focus-within:ring-accent-ring')}
    >
      {money && <span aria-hidden className="mr-1.5 shrink-0 text-body text-text-faint">$</span>}
      <input
        aria-label={label}
        value={value}
        inputMode="decimal"
        onChange={(event) => onChange(event.target.value.replace(/[^\d.-]/g, ''))}
        className="tnum w-full min-w-0 bg-transparent text-row font-semibold text-text placeholder:font-normal placeholder:text-text-faint focus:outline-none focus-visible:shadow-none"
        placeholder="Any"
      />
    </label>
  )
}

// ---------------------------------------------------------------------------
// Boolean and text
// ---------------------------------------------------------------------------

export function BooleanControl({ field, value, onChange }: KindControlProps) {
  const current = value === null ? 'any' : value.value === true || value.value === 'true'
    ? 'yes' : 'no'

  return (
    <Segmented
      size="large"
      full
      label={field.label}
      value={current}
      onChange={(next) => onChange(next === 'any'
        ? null
        : { field: field.name, op: 'eq', value: next === 'yes' })}
      options={[
        { value: 'any', label: 'Any' },
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
      ]}
    />
  )
}

/** The operators a text box offers, in the order the mockup lists them. */
const TEXT_OPERATORS: FilterOperator[] = [
  'contains', 'starts_with', 'eq', 'neq', 'is_null', 'is_not_null',
]

const TEXT_OPERATOR_LABELS: Record<string, string> = {
  contains: 'contains', starts_with: 'starts with', eq: 'is', neq: 'is not',
  is_null: 'is empty', is_not_null: 'is not empty',
}

export function TextControl({ field, value, onChange }: KindControlProps) {
  const op = value?.op ?? 'contains'
  const nullary = NULLARY_OPERATORS.includes(op)

  return (
    <div className="flex items-center gap-1.5">
      <MenuSelect
        label={`How to match ${field.label}`}
        value={op}
        onChange={(next) => {
          if (!next) return
          const chosen = next as FilterOperator
          if (NULLARY_OPERATORS.includes(chosen)) {
            onChange({ field: field.name, op: chosen })
            return
          }
          const text = String(value?.value ?? '')
          onChange(text ? { field: field.name, op: chosen, value: text } : null)
        }}
        options={TEXT_OPERATORS.map((entry) => ({
          value: entry, label: TEXT_OPERATOR_LABELS[entry] ?? entry,
        }))}
        minWidth={150}
        className="w-[130px] shrink-0"
      />
      {!nullary && (
        <label className={cn('flex h-[34px] min-w-0 flex-1 items-center rounded-control',
          'border border-line bg-surface px-2.5 focus-within:ring-2 focus-within:ring-accent-ring')}
        >
          <input
            aria-label={field.label}
            value={String(value?.value ?? '')}
            onChange={(event) => onChange(event.target.value
              ? { field: field.name, op, value: event.target.value }
              : null)}
            placeholder="Any"
            className="w-full min-w-0 bg-transparent text-cell text-text placeholder:text-text-faint focus:outline-none focus-visible:shadow-none"
          />
        </label>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// The one entry point
// ---------------------------------------------------------------------------

export function KindControl(props: KindControlProps): ReactNode {
  switch (props.field.kind) {
    case 'enum': return <EnumControl {...props} />
    case 'date': case 'datetime': return <DateControl {...props} />
    case 'relation': return <RelationControl {...props} />
    case 'money': case 'number': return <RangeControl {...props} />
    case 'boolean': return <BooleanControl {...props} />
    /*
     * A `uuid` that the catalogue has not marked as a relation is still an
     * id, and the only honest thing to offer for one is a text box. Marking
     * it `relation` in the `AreaDef` is what turns it into a search.
     */
    default: return <TextControl {...props} />
  }
}

// ---------------------------------------------------------------------------
// What a section, and a toolbar chip, says it is set to
// ---------------------------------------------------------------------------

/**
 * The right-hand summary on a section row — "Quoted, Approved", "Last 30
 * days", "$2,500 and up".
 *
 * One function, because the popover's section, the toolbar chip and the phone
 * sheet's row all print it, and three of them would eventually disagree about
 * what "is between" reads as.
 */
export function summaryOf(
  field: ListFieldDef, condition: FilterCondition | null,
  cache?: ReadonlyMap<string, string | null>,
): string {
  if (!condition) return 'Any'
  const money = field.kind === 'money'
  const amount = (value: unknown) =>
    (money ? `$${formatNumber(Number(value))}` : formatNumber(Number(value)))

  /*
   * **A summary never shows an id** (v3 §4.3), the same rule the chips and
   * the sentence follow — and the same three answers: the name, "a company
   * you can't see" when the lookup came back without it, and an ellipsis
   * while it is still out. `cache` is optional only because a few callers
   * have a field and a condition and no list; where it is absent the id is
   * still never printed.
   */
  const named = (value: unknown): string => {
    if (typeof value !== 'string') return humanValue(value)
    if (field.kind !== 'relation' && field.kind !== 'uuid') return humanValue(value)
    const name = labelFor(condition, value, cache)
    if (name !== undefined) return name ?? unseenText(field.relation?.label)
    return '\u2026'
  }

  switch (condition.op) {
    case 'in': case 'not_in': {
      const values = Array.isArray(condition.value) ? condition.value : [condition.value]
      const names = values.map((one) =>
        (isToken(one) ? TOKEN_LABELS[one.token] : named(one)))
      const head = condition.op === 'not_in' ? 'Not ' : ''
      // Three names is a summary; eight is the chips again, in a smaller font.
      return head + (names.length > 3 ? `${names.length} picked` : names.join(', '))
    }
    case 'in_period': {
      const period = datePeriod.safeParse(condition.value)
      return period.success ? describePeriod(period.data) : 'A date window'
    }
    case 'in_relative': {
      const window = relativeWindow.safeParse(condition.value)
      if (!window.success) return 'A date window'
      return window.data.direction === 'ago'
        ? humanValue(describeRelative(window.data))
        : `In ${describeRelative(window.data)}`
    }
    case 'between': {
      const range = Array.isArray(condition.value) ? condition.value : []
      return `${amount(range[0])} – ${amount(range[1])}`
    }
    case 'gte': return field.numeric ? `${amount(condition.value)} and up` : `From ${condition.value}`
    case 'lte': return field.numeric ? `Up to ${amount(condition.value)}` : `Until ${condition.value}`
    case 'gt': return `Over ${amount(condition.value)}`
    case 'lt': return `Under ${amount(condition.value)}`
    case 'is_null': return 'Empty'
    case 'is_not_null': return 'Not empty'
    case 'contains': return `Contains ${condition.value}`
    case 'starts_with': return `Starts with ${condition.value}`
    case 'neq': return `Not ${field.numeric ? amount(condition.value) : named(condition.value)}`
    case 'eq': {
      if (field.kind === 'boolean') return condition.value === true ? 'Yes' : 'No'
      if (isToken(condition.value)) return TOKEN_LABELS[condition.value.token]
      // A money column says $0, not 0 — the same words the sentence uses.
      if (field.numeric) return amount(condition.value)
      return named(condition.value)
    }
  }
}
